import { NotificationEnum } from '@/assets/global/enums/endpoints/NotificationEnum'

export interface NotificationSender {
  id: string
  name: string
  photo: string | null
}

export interface NotificationItem {
  id: string
  type: string
  title: string
  description: string | null
  targetRoute: string | null
  isRead: boolean
  createdAt: string
  sender: NotificationSender | null
}

export type NotificationStatusFilter = 'all' | 'unread' | 'read'

export interface ListNotificationsParams {
  page?: number
  limit?: number
  status?: NotificationStatusFilter
}

export interface ListNotificationsResponse {
  data: {
    items: NotificationItem[]
    totalItems: number
    page: number
    limit: number
    hasMoreResults: boolean
  }
}

export interface CountUnreadResponse {
  data: {
    count: number
  }
}

export interface MarkAllAsReadResponse {
  data: {
    count: number
  }
}

export interface DeleteAllReadResponse {
  data: {
    deletedCount: number
  }
  message: string
}

export function useNotifications() {
  const listNotifications = async (params: ListNotificationsParams = {}) => {
    const query: Record<string, unknown> = {}

    if (params.page !== undefined) query.page = params.page
    if (params.limit !== undefined) query.limit = params.limit
    if (params.status !== undefined) query.status = params.status

    const { data, error, response } = await useApi(
      createUrl(NotificationEnum.LIST, { query }),
    )
      .get()
      .json<ListNotificationsResponse>()

    return { data, error, response }
  }

  const countUnread = async () => {
    const { data, error, response } = await useApi(
      createUrl(NotificationEnum.COUNT_UNREAD),
    )
      .get()
      .json<CountUnreadResponse>()

    return { data, error, response }
  }

  const markAsRead = async (notificationId: string) => {
    const { data, error, response } = await useApi(
      createUrl(
        NotificationEnum.MARK_AS_READ.replace(
          '{notificationId}',
          notificationId,
        ),
      ),
    )
      .patch()
      .json()

    return { data, error, response }
  }

  const markAllAsRead = async () => {
    const { data, error, response } = await useApi(
      createUrl(NotificationEnum.MARK_ALL_AS_READ),
    )
      .post()
      .json<MarkAllAsReadResponse>()

    return { data, error, response }
  }

  const deleteNotification = async (notificationId: string) => {
    const { data, error, response } = await useApi(
      createUrl(
        NotificationEnum.DELETE_ONE.replace('{notificationId}', notificationId),
      ),
    )
      .delete()
      .json()

    return { data, error, response }
  }

const deleteAllReadNotifications = async () => {
    const { data, error, response } = await useApi(
      createUrl(NotificationEnum.DELETE_ALL_READ),
    )
      .delete()
      .json<DeleteAllReadResponse>()

    return { data, error, response }
  }

  return { listNotifications, countUnread, markAsRead, markAllAsRead, deleteNotification, deleteAllReadNotifications }
}
